import axios from 'axios';
import { utilityService } from '@/services/utilityService.js';
import { StudyIds } from '@/common/Studies.js';

/**
 * Service to interact with the researcher api.
 */
export const researcherApiService = {
	loadStudy: (studyUuid) => __loadStudy(studyUuid),
	loadUserInfo: (userId) => __loadUserInfo(userId),
	saveUserInfo: (user) => __saveUserInfo(user),
	saveStudies: (studies) => __saveStudies(studies),
	deleteStudy: (studyUuid) => __deleteStudy(studyUuid),
	deleteRecalls: (studyUuid, recallUuids) => __deleteRecalls(studyUuid, recallUuids),
	checkAbbreviation: (abbreviation) => __checkAbbreviation(abbreviation),
	createRespondentsFromFile: (uploadedFile, studyUuid) => __createRespondentsFromFile(uploadedFile, studyUuid),
	loadRespondentFiles: (studyUuid) => __loadRespondentFiles(studyUuid),
	loadFile: (studyUuid, fileName) => __loadFile(studyUuid, fileName),
	deleteFile: (studyUuid, fileName) => __deleteFile(studyUuid, fileName),
	loadStaff: (studyUuid) => __loadStaff(studyUuid),
	addExistingStaff: (studyUuid, staffEmail) => __addExistingStaff(studyUuid, staffEmail),
	addNewStaff: (studyUuid, newStaffInfo) => __addNewStaff(studyUuid, newStaffInfo),
	removeStaffFromStudy: (studyUuid, userUuid) => __removeStaffFromStudy(studyUuid, userUuid),
	loadRespondentAccounts: (studyUuid) => __loadRespondentAccounts(studyUuid),
	deleteRespondentAccounts: (studyUuid, accountInformation) => __deleteRespondentAccounts(studyUuid, accountInformation),
	updateUser: (userUuid, fields) => __updateUser(userUuid, fields),
	register: (registerInfo) => __register(registerInfo),
	wizardCreateRespondents: (studyUuid, respondentsInfo) => __wizardCreateRespondents(studyUuid, respondentsInfo),
	contact: (contactInfo, attachment) => __contact(contactInfo, attachment),
	getStudyVersions: () => __getStudyVersions(),
	searchUser: (email, researcher) => __searchUser(email, researcher),
	searchStudy: (studyUuid) => __searchStudy(studyUuid),
	updateProfile: (userUuid, fields) => __updateProfile(userUuid, fields),
	loadTrackRespondents: (studyUuid, userName) => __loadTrackRespondents(studyUuid, userName),
	getUsersRemaingScheduledRecalls: (userUuid, currentRecallNumber) => __getUsersRemaingScheduledRecalls(userUuid, currentRecallNumber),
	saveAdminMessage: (messageInfo, isTest) => __saveAdminMessage(messageInfo, isTest),
	updateAdminMessage: (messageUuid, messageBody) => __updateAdminMessage(messageUuid, messageBody),
	getBanners: () => __getBanners(),
	getBlogs: () => __getBlogs(),
	getAllMessages: () => __getAllMessages(),
	deleteMessage: (messageUuid) => __deleteMessage(messageUuid),
	getLogs: (page, size, sortBy, sortDesc, logType, month, year, userName, level) => __getLogs(page, size, sortBy, sortDesc, logType, month, year, userName, level),
	getAuditTrail: (page, size, sortBy, sortDesc, logType, month, year, userName) => __getAuditTrail(page, size, sortBy, sortDesc, logType, month, year, userName),
	getUserAccountInfo: (userUuid) => __getUserAccountInfo(userUuid),
	adminUpdateUserInfo: (userUuid, fields) => __adminUpdateUserInfo(userUuid, fields),
	getUsersSubscribedToEmailNotifications: () => __getUsersSubscribedToEmailNotifications(),
	addNonStudySpecificRole: (userUuid, role) => __addNonStudySpecificRole(userUuid, role),
	deleteNonStudySpecificRole: (userUuid, role) => __deleteNonStudySpecificRole(userUuid, role),
	deleteResearcher: (userUuid) => __deleteResearcher(userUuid),
	saveStudyAdmin: (studyUuid, studyChanges) => __saveStudyAdmin(studyUuid, studyChanges)
};

/**
 * Gets the studies that the user is a part of based on token
 */
function __loadStudy (studyUuid) {
	return axios.get(utilityService.getResearcherApiUrl(`study/${studyUuid}`),
		{
			headers: utilityService.getHeaders()
		}
	);
}

/**
 * Gets te user information based on token
 */
function __loadUserInfo (userId) {
	return axios.get(utilityService.getResearcherApiUrl(`user/${userId}`),
		{
			headers: utilityService.getHeaders()
		}
	).then(response => {
		return new Promise((resolve, reject) => {
			response.data.studyRoles = response.data.studyRoles.filter(studyRole => studyRole.studyUuid !== StudyIds.ResearcherStudyId);
			console.log(response.data);
			resolve(response);
		});
	}).catch(error => {
		return new Promise((resolve, reject) => {
			reject(error);
		});
	});
}

/**
 * Updates the current user's information to the given values
 * @param {Object} user the user information to update
 */
function __saveUserInfo (userList) {
	return axios.post(utilityService.getResearcherApiUrl('user/'),
		userList,
		{
			headers: utilityService.getHeaders()
		}
	);
}

/**
 * Creates or updates studies based on studyUuid
 * @param {array} studies an array of study objects to be updated or created
 */
function __saveStudies (studies) {
	return axios.post(utilityService.getResearcherApiUrl('study/'),
		studies,
		{
			headers: utilityService.getHeaders()
		}
	);
}

/**
 * Deletes the study with the given studyUuid
 * @param {String} studyUuid the internal id of the study
 */
function __deleteStudy (studyUuid) {
	return axios.delete(utilityService.getResearcherApiUrl(`study/${studyUuid}`),
		{
			headers: utilityService.getHeaders()
		}
	);
}

/**
 * Deletes the recalls with the given recallUuids
 * @param {String} studyUuid the internal id of the study
 * @param {String} recallUuids a list of recall identifiers to be deleted
 */
function __deleteRecalls (studyUuid, recallUuids) {
	return axios.post(utilityService.getResearcherApiUrl(`study/${studyUuid}/recall`),
		recallUuids,
		{
			headers: utilityService.getHeaders()
		}
	);
}

/**
 * Creates respondent accounts based on the information in the file uploaded by the user
 * @param {file} uploadedFile The file with the respondents data to be created
 * @param {String} studyUuid The study id that the respondents are to be created on
 * @returns The user information of the created users
 */
function __createRespondentsFromFile (uploadedFile, studyUuid) {
	var formData = new FormData();
	formData.append('file', uploadedFile);
	return axios.post(utilityService.getResearcherApiUrl(`study/${studyUuid}/respondents/files?fileType=UPLOAD`),
		formData,
		{
			headers: utilityService.getHeaders()
		});
}

/**
 * Gets the information of the files that are stored in the API for the given study id
 * @param {String} studyUuid The study id to get the files from
 * @returns the files information for display/retrieval purposes (not the files themselves)
 */
function __loadRespondentFiles (studyUuid) {
	return axios.get(utilityService.getResearcherApiUrl(`study/${studyUuid}/respondents/files`),
		{
			headers: utilityService.getHeaders()
		});
}

/**
 * Gets a file based on the file name that is passed
 * @param {String} studyUuid The study id to get the files from
 * @param {String} fileName the name of the file to download
 */
function __loadFile (studyUuid, fileName) {
	return axios.get(utilityService.getResearcherApiUrl(`study/${studyUuid}/respondents/files/${fileName}`),
		{
			headers: utilityService.getHeaders()
		});
}

/**
 * Gets a file based on the file name that is passed
 * @param {String} studyUuid The study id to get the files from
 * @param {String} fileName the name of the file to delete
 */
function __deleteFile (studyUuid, fileName) {
	return axios.delete(utilityService.getResearcherApiUrl(`study/${studyUuid}/respondents/files/${fileName}`),
		{
			headers: utilityService.getHeaders()
		});
}

/**
 * Gets a list of users that have access to the given study
 * @param {String} studyUuid the studyUuid that the users have access to
 * @returns a list of users
 */
function __loadStaff (studyUuid) {
	return axios.get(utilityService.getResearcherApiUrl(`study/${studyUuid}/staff`),
		{
			headers: utilityService.getHeaders()
		});
}

/**
 * Gives access to the given user to the given study
 * @param {String} studyUuid the studyUuid to give access to
 * @param {String} staffEmail the existing user email
 * @returns Whether or not the user was added to the study
 */
function __addExistingStaff (studyUuid, staffEmail) {
	return axios.post(utilityService.getResearcherApiUrl(`study/${studyUuid}/staff/existing`),
		{ staffEmail: staffEmail },
		{
			headers: utilityService.getHeaders()
		});
}

/**
 * Creates a new user and gives them access to the given study
 * @param {String} studyUuid study to give access to
 * @param {Object} newStaffInfo user info needed to create a new user
 */
function __addNewStaff (studyUuid, newStaffInfo) {
	return axios.post(utilityService.getResearcherApiUrl(`study/${studyUuid}/staff`),
		newStaffInfo,
		{
			headers: utilityService.getHeaders()
		});
}

/**
 * Removes the given user from the given study
 * @param {String} studyUuid the study to remove from
 * @param {String} userUuid the user to remove from the study
 * @returns Whether the user was successfully removed
 */
function __removeStaffFromStudy (studyUuid, userUuid) {
	return axios.delete(utilityService.getResearcherApiUrl(`study/${studyUuid}/staff/${userUuid}`),
		{
			headers: utilityService.getHeaders()
		});
}

/**
 * Gets the respondents that are tied to the given study
 * @param {String} studyUuid the study to load respondents from
 * @returns A list of respondent users
 */
function __loadRespondentAccounts (studyUuid) {
	return axios.get(utilityService.getResearcherApiUrl(`study/${studyUuid}/respondents`),
		{
			headers: utilityService.getHeaders()
		});
}

/**
 * Deletes the given respondents from the given study
 * @param {String} studyUuid the study to delete respondents from
 * @param {Object} accountInformation object containing a list of user ids
 */
function __deleteRespondentAccounts (studyUuid, accountInformation) {
	return axios.delete(utilityService.getResearcherApiUrl(`study/${studyUuid}/respondents`),
		{
			headers: utilityService.getHeaders(),
			data: accountInformation
		}
	);
}

/**
 * Updates the given fields for the given user
 * @param {String} userUuid The user to update
 * @param {Object} fields The fields to update
 * @returns The updated user object
 */
function __updateUser (userUuid, fields) {
	return axios.put(utilityService.getResearcherApiUrl(`user/${userUuid}/`),
		fields,
		{
			headers: utilityService.getHeaders()
		}
	);
}

/**
 * Checks for the given abbreviation to be unique
 * @param {string} abbreviation the studyId or abbreviation of the study
 * @returns whether or not the abbreviation can be used. If invalid, gives some
 * 					suggested abbreviations.
 */
function __checkAbbreviation (abbreviation) {
	return axios.post(utilityService.getResearcherApiUrl(`study/validate/abbreviation?abbreviation=${abbreviation}`),
		null,
		{
			headers: utilityService.getHeaders()
		}
	);
}

function __wizardCreateRespondents (studyUuid, respondentsInfo) {
	return axios.post(utilityService.getResearcherApiUrl(`study/${studyUuid}/respondents/wizard`),
		respondentsInfo,
		{
			headers: utilityService.getHeaders()
		}
	);
}
function __register (registerInfo) {
	return axios.post(utilityService.getResearcherApiUrl('user/register'),
		registerInfo
		,
		{
			headers: utilityService.getHeaders()
		}
	)
}

function __contact (contactInfo, attachment) {
	const formData = new FormData();
	formData.append('contactInfo', JSON.stringify(contactInfo));
	formData.append('attachment', attachment);
	//  const formData = {"contactInfo": JSON.stringify(contactInfo), "attachment": attachment}
	console.log('formData: ', formData)

	return axios.post(utilityService.getResearcherApiUrl('admin/contact'),
		formData
		,
		{
			headers: utilityService.getHeaders()
		}
	)
}

function __updateProfile (userUuid, fields) {
	return axios.put(utilityService.getResearcherApiUrl(`user/${userUuid}/`),
		// userDetails,
		fields,
		{
			headers: utilityService.getHeaders()
		}
	);
}

/**
 * Gets a list of all defined versions for studies.
 */
function __getStudyVersions () {
	return axios.get(utilityService.getResearcherApiUrl('study/versions'),
		{
			headers: utilityService.getHeaders()
		}
	);
}

function __searchUser (email, researcher, fields) {
	// return axios.get(utilityService.getResearcherApiUrl(`/user/search?userName=${email}&role=RESEARCHER`),
	return axios.get(utilityService.getResearcherApiUrl(`/user/search?userName=${email}`),
		// fields,
		{
			headers: utilityService.getHeaders()
		}
	);
}

function __searchStudy (studyId, fields) {
	return axios.get(utilityService.getResearcherApiUrl(`/study/search?studyId=${studyId}`),
		// fields,
		{
			headers: utilityService.getHeaders()
		}
	);
}

function __loadTrackRespondents (studyUuid, userName) {
	let queryParams = '';
	if (userName && userName.length > 0) {
		queryParams = `?userName=${userName}`;
	}
	return axios.get(utilityService.getResearcherApiUrl(`study/${studyUuid}/track/respondents${queryParams}`),
		{
			headers: utilityService.getHeaders()
		}
	);
}

function __getUsersRemaingScheduledRecalls (userUuid, currentRecallNumber) {
	return axios.get(utilityService.getResearcherApiUrl(`user/${userUuid}/recalls/scheduled?currentRecallNumber=${currentRecallNumber}`),
		{
			headers: utilityService.getHeaders()
		}
	);
}

function __saveAdminMessage (messageInfo, isTest) {
	return axios.post(utilityService.getResearcherApiUrl('message/'),
		{ messages: [messageInfo], isTest: isTest },
		{
			headers: utilityService.getHeaders()
		}
	);
}

function __updateAdminMessage (messageUuid, messageBody) {
	return axios.post(utilityService.getResearcherApiUrl(`message/${messageUuid}`),
		{ messageBody: messageBody },
		{
			headers: utilityService.getHeaders()
		}
	);
}

function __getLogs (page, size, sortBy, sortDesc, logType, month, year, userName, level) {
	const direction = sortDesc ? 'DESC' : 'ASC';
	return axios.get(utilityService.getResearcherApiUrl('logs/'),
		{
			params: { page: page - 1, size: size, sort: sortBy, direction: direction, logType: logType, month: month, year: year, userName: userName, level: level },
			headers: utilityService.getHeaders()
		}
	);
}

function __getAuditTrail (page, size, sortBy, sortDesc, logType, month, year, userName) {
	const direction = sortDesc ? 'DESC' : 'ASC';
	return axios.get(utilityService.getResearcherApiUrl('logs/audittrail'),
		{
			params: { page: page - 1, size: size, sort: sortBy, direction: direction, logType: logType, month: month, year: year, userName: userName },
			headers: utilityService.getHeaders()
		}
	);
}

function __getBanners () {
	return axios.get(utilityService.getResearcherApiUrl('message/current'),
		{
			headers: utilityService.getHeaders()
		});
}

function __getUserAccountInfo (userUuid) {
	return axios.get(utilityService.getResearcherApiUrl(`user/${userUuid}/account`),
		{
			headers: utilityService.getHeaders()
		});
}

function __getBlogs () {
	return axios.get(utilityService.getResearcherApiUrl('message/blogs'),
		{
			headers: utilityService.getHeaders()
		})
}

function __getAllMessages () {
	return axios.get(utilityService.getResearcherApiUrl('message/all'),
		{
			headers: utilityService.getHeaders()
		})
}

function __deleteMessage (messageUuid) {
	return axios.delete(utilityService.getResearcherApiUrl(`message/${messageUuid}`),
		{
			headers: utilityService.getHeaders()
		});
}

function __adminUpdateUserInfo (userUuid, fields) {
	console.log(fields);
	return axios.post(utilityService.getResearcherApiUrl(`admin/user/${userUuid}`),
		fields,
		{
			headers: utilityService.getHeaders()
		});
}

function __getUsersSubscribedToEmailNotifications () {
	return axios.get(utilityService.getResearcherApiUrl('user/subscriptions/email'),
		{
			headers: utilityService.getHeaders()
		});
}

/**
 * Adds a role to the user. The role is a top level access role. Not a study specific role.
 * @param {String} userUuid - The user uuid
 * @param {String} role - The role to assign to the user. Valid roles are: ADMIN, RESEARCHERAPI, PROBE_BUILDER
 * @returns {Promise} - A promise that resolves to the updated user object.
 */
function __addNonStudySpecificRole (userUuid, role) {
	return axios.post(utilityService.getResearcherApiUrl(`user/${userUuid}/roles/${role}`),
		{
			headers: utilityService.getHeaders()
		});
}

/**
 * Removes a role from the user. The role is a top level access role. Not a study specific role.
 * @param {String} userUuid - The user uuid
 * @param {String} role - The role to assign to the user. Valid roles are: ADMIN, RESEARCHERAPI, PROBE_BUILDER
 * @returns {Promise} - A promise that resolves to the updated user object.
 */
function __deleteNonStudySpecificRole (userUuid, role) {
	return axios.post(utilityService.getResearcherApiUrl(`user/${userUuid}/roles/${role}`),
		{
			headers: utilityService.getHeaders()
		});
}

/**
 * Deletes a researcher type user. This is an admin only function.
 * @param {String} userUuid
 * @returns {Promise} - A promise that resolves to the updated user object.
 */
function __deleteResearcher (userUuid) {
	return axios.delete(utilityService.getResearcherApiUrl(`admin/researcher/${userUuid}`),
		{
			headers: utilityService.getHeaders()
		});
}

/*
 * Updates the study with configuration changes available to admins.
 * @param {*} studyUuid - The study uuid
 * @param {*} studyChanges - The study changes
 * @returns {Promise} - A promise that resolves to the updated study object.
 */
function __saveStudyAdmin (studyUuid, studyChanges) {
	return axios.post(utilityService.getResearcherApiUrl(`admin/study/${studyUuid}`),
		studyChanges,
		{
			headers: utilityService.getHeaders()
		});
}
